import React, { useState, useEffect } from "react";
import { Modal, Row, Col, Tag, Divider, Button, Input } from "antd";
import {
  StarFilled,
  LikeOutlined,
  PlayCircleOutlined,
  ClockCircleOutlined,
  AudioOutlined,
  PlayCircleOutlined as PlayIcon,
  PauseCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import VideosPlayer from "./VideosPlayer";
import { CustomButton, CustomSecondaryButton } from "../physician_page/styled";
import styled from "styled-components";
import ModalWrapper from "../../modal/ModelWrapper";
import videosService from "../../../services/videos";
import { notify } from "../../../utils/helper";

const ModalContent = styled.div`
  .ant-modal-body {
    padding: 0;
    max-height: 70vh;
  }
`;

const ScrollableContent = styled.div`
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
`;

const VideoDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const VideoHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const VideoTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: var(--primary-text-color);
  margin: 0;
  line-height: 1.3;
`;

const VideoMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #6b7280;
  font-size: 14px;
`;

const VideoDescription = styled.div`
  font-size: 16px;
  color: var(--secondary-text-color);
  line-height: 1.6;
  margin: 0;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 16px 0;
`;

const SpecialtyContainer = styled.div`
  background: #f8fafc;
  padding: 16px;
  border-radius: 12px;
  border-left: 4px solid #3b82f6;
`;

const SpecialtyLabel = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 4px;
`;

const SpecialtyValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--primary-text-color);
`;

const ActionContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #f3f4f6;
`;

const RecordingContainer = styled.div`
  background: #f8fafc;
  padding: 16px;
  border-radius: 12px;
  border-left: 4px solid #10b981;
  margin: 16px 0;
`;

const RecordingHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const RecordingTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--primary-text-color);
`;

const RecordingControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const CustomTagContainer = styled.div`
  background: #fefcf3;
  padding: 16px;
  border-radius: 12px;
  border-left: 4px solid #f59e0b;
  margin: 16px 0;
`;

const CustomTagHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const CustomTagTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--primary-text-color);
`;

const CustomTagControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const VideoDetailModal = ({
  visible,
  setVisible,
  onClose,
  videoData,
  onAttach,
  selectedRecording,
  recordingFileUrl,
  recordingFileName,
  getRecordingFileFromS3,
  setOpen,
}) => {
  const [customTags, setCustomTags] = useState([]);
  const [inputVisible, setInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isAttaching, setIsAttaching] = useState(false);



  useEffect(() => {
    if (videoData?.customTags) {
      setCustomTags(videoData.customTags);
    } else {
      setCustomTags([]);
    }
  }, [videoData]);

  if (!videoData) return null;

  const handleAttachRecording = async () => {
    try {
      setIsAttaching(true);
      const payload = {
        recordingId: selectedRecording.id,
        videoId: videoData.id,
        customTags: customTags,
      };
      const response = await videosService.createPictoryVideo(payload);
      // console.log("Pictory video created successfully:", response);
      
      setTimeout(() => {
        notify("success", response?.data?.message);
        onClose();
        setIsAttaching(false);
      }, 1500);

    } catch (error) {
      setIsAttaching(false);
      notify("error", error?.response?.data?.message);
    }
  };

  const handleTagClose = (removedTag) => {
    const newTags = customTags.filter((tag) => tag !== removedTag);
    setCustomTags(newTags);
  };

  const showTagInput = () => {
    setInputVisible(true);
  };

  const handleTagInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleTagInputConfirm = () => {
    if (inputValue && customTags.indexOf(inputValue) === -1) {
      setCustomTags([...customTags, inputValue]);
    }
    setInputVisible(false);
    setInputValue("");
  };

  const handleTagInputKeyPress = (e) => {
    if (e.key === "Enter") {
      handleTagInputConfirm();
    }
  };

  return (
    <ModalWrapper
      title={
        <h3 className="font-18 font-semibold text-primary">
          Create Clone Video
        </h3>
      }
      open={visible}
      setShow={setVisible}
      onCancel={() => {
        setVisible(false)
        onClose()
      }}
      onClose={() => {
        setVisible(false)
        onClose()
      }}
      footer={null}
      width={600}
      centered
    >
      <ModalContent>
        <ScrollableContent>
          <VideoDetailContainer>
            <div style={{ marginBottom: 5 }}>
              <VideosPlayer
                videoUrl={videoData.videoUrl}
                title={videoData.title}
              />
            </div>

            {selectedRecording && (
              <RecordingContainer>
                <RecordingHeader>
                  <AudioOutlined
                    style={{ color: "#10b981", fontSize: "18px" }}
                  />
                  <RecordingTitle>Selected Recording</RecordingTitle>
                </RecordingHeader>
                <RecordingControls>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      {selectedRecording.recordingTitle}
                    </div>
                    <div style={{ fontSize: "13px", color: "#6b7280" }}>
                      Recorded by: {selectedRecording.recordingBy} •{" "}
                      {selectedRecording.createdAt}
                    </div>
                  </div>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    {recordingFileUrl &&
                      recordingFileName === selectedRecording.recording && (
                        <audio
                          controls
                          src={recordingFileUrl}
                          style={{ maxWidth: "200px", height: "32px" }}
                        />
                      )}
                    <Button
                      type="default"
                      size="small"
                      icon={
                        recordingFileUrl &&
                        recordingFileName === selectedRecording.recording ? (
                          <PauseCircleOutlined />
                        ) : (
                          <PlayIcon />
                        )
                      }
                      onClick={() =>
                        getRecordingFileFromS3(selectedRecording.recording)
                      }
                    >
                      {recordingFileUrl &&
                      recordingFileName === selectedRecording.recording
                        ? "Pause"
                        : "Play"}
                    </Button>
                  </div>
                </RecordingControls>
              </RecordingContainer>
            )}

            {videoData.specialty && (
              <SpecialtyContainer>
                <SpecialtyLabel>Medical Specialty</SpecialtyLabel>
                <SpecialtyValue>{videoData.specialty}</SpecialtyValue>
              </SpecialtyContainer>
            )}

            {videoData.tags && videoData.tags.length > 0 && (
              <div>
                <h4
                  style={{
                    marginBottom: 12,
                    color: "var(--primary-text-color)",
                  }}
                >
                  Topics Covered
                </h4>
                <TagsContainer>
                  {videoData.tags.map((tag, index) => (
                    <Tag
                      key={index}
                      color="blue"
                      style={{ padding: "4px 12px", fontSize: "13px" }}
                    >
                      {tag}
                    </Tag>
                  ))}
                </TagsContainer>
              </div>
            )}

            <CustomTagContainer>
              <CustomTagHeader>
                <PlusOutlined style={{ color: "#f59e0b", fontSize: "18px" }} />
                <CustomTagTitle>Custom Tags</CustomTagTitle>
              </CustomTagHeader>
              <CustomTagControls>
                {customTags.map((tag, index) => (
                  <Tag
                    key={index}
                    closable
                    onClose={() => handleTagClose(tag)}
                    color="orange"
                    style={{
                      margin: 0,
                      padding: "4px 8px",
                      borderRadius: "6px",
                      fontWeight: 500,
                    }}
                  >
                    {tag}
                  </Tag>
                ))}
                {inputVisible && (
                  <Input
                    type="text"
                    size="small"
                    style={{
                      height: "28px",
                      width: "120px",
                      borderRadius: "6px",
                    }}
                    value={inputValue}
                    onChange={handleTagInputChange}
                    onBlur={handleTagInputConfirm}
                    onKeyPress={handleTagInputKeyPress}
                    placeholder="Enter tag"
                    autoFocus
                  />
                )}
                {!inputVisible && (
                  <Button
                    size="small"
                    type="dashed"
                    onClick={showTagInput}
                    icon={<PlusOutlined />}
                    style={{
                      height: "28px",
                      borderColor: "#f59e0b",
                      backgroundColor: "#fefcf3",
                      color: "#d97706",
                    }}
                  >
                    Add Custom Tag
                  </Button>
                )}
              </CustomTagControls>
            </CustomTagContainer>

            <ActionContainer>
              <CustomSecondaryButton onClick={onClose}>
                Cancel
              </CustomSecondaryButton>
              <CustomButton onClick={handleAttachRecording} loading={isAttaching}>
                {selectedRecording
                  ? "Create Clone Video"
                  : "Creating..."}
              </CustomButton>
            </ActionContainer>
          </VideoDetailContainer>
        </ScrollableContent>
      </ModalContent>
    </ModalWrapper>
  );
};

export default VideoDetailModal;
